import {
    Checkbox, Form, Input, InputNumber,
} from 'antd';
import {CheckboxChangeEvent} from 'antd/es/checkbox';
import {FormInstance} from 'antd/es/form';
import {FormListFieldData} from 'antd/es/form/FormList';
import cn from 'classnames';
import React from 'react';

import {CustomSelect, ECustomSelectMode} from 'components/form/inputs/custom-select';

import {TTableAttributesCustomSelectEntry} from '../../../report-table-settings';
import {GROUPING_SETTINGS_PREFIX} from '../dd-grouping-settings-modal-opener-constants';

interface IDDGroupingSettingsModalFormRowProps {
  form: FormInstance<any>;
  fields: FormListFieldData[];
  attributesEntries: TTableAttributesCustomSelectEntry[];
  extendedAttributesEntries: TTableAttributesCustomSelectEntry[] | undefined;
  templateCode: string | null;
  handleCheck: (e: CheckboxChangeEvent) => void;
}

export const DDGroupingSettingsModalFormRow = ({
    form,
    fields,
    attributesEntries,
    extendedAttributesEntries,
    templateCode,
    handleCheck,
}: IDDGroupingSettingsModalFormRowProps) => (
    <div className={cn('form-list')}>
        {fields?.map(field => (
            <div
                key={field.fieldKey}
                className={cn('form-list__items')}
            >
                <Checkbox
                    name="field-index"
                    value={field.name}
                    onChange={handleCheck}
                />
                <Form.Item
                    rules={[{required: true}]}
                    style={{width: 280}}
                    label="Группирующий атрибут"
                    name={[field.name, 'keyName']}
                >
                    <CustomSelect
                        entries={extendedAttributesEntries}
                        settings={{
                            placeholder: 'Выберите атрибут',
                            isClearable: true,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
                <Form.Item
                    style={{width: 280}}
                    label="Группируемый атрибут"
                    name={[field.name, 'groupedColumnNames']}
                    rules={[
                        {required: true},
                    ]}
                >
                    <CustomSelect
                        entries={attributesEntries}
                        settings={{
                            formInstance: form,
                            formFieldKey: 'groupedColumnNames',
                            placeholder: 'Выберите атрибуты',
                            isClearable: true,
                            mode: ECustomSelectMode.multiple,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
                <Form.Item
                    style={{width: 170}}
                    label="Порядок группировки"
                    name={[field.name, 'hierarchyLevel']}
                    rules={[
                        {required: true},
                        ({getFieldValue}) => ({
                            validator: (_, value) => {
                                const groupingValues = getFieldValue(
                                    GROUPING_SETTINGS_PREFIX,
                                );
                                const sameOrdinals = groupingValues
                                    ?.filter((entry: any) => !!value
                        && entry?.hierarchyLevel === value);
                                if (sameOrdinals?.length > 1) {
                                    return Promise.reject(new Error(
                                        'Уже существует',
                                    ));
                                } return Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <InputNumber placeholder="Введите значение" />
                </Form.Item>
                <Form.Item
                    rules={[{required: true}, {
                        max: 100,
                        message: 'Максимальная длина — 100 символов',
                    }]}
                    style={{width: 226}}
                    label="Наименование итога/подытога"
                    name={[field.name, 'groupLabel']}
                >
                    <Input placeholder="Введите значение" />
                </Form.Item>
                <Form.Item
                    rules={[{required: true}]}
                    style={{width: 226}}
                    label="Агр. функция"
                    name={[field.name, 'aggFunction']}
                >
                    <CustomSelect
                        settings={{
                            url: 'lookupValue/TABLE_REPORT_AGG_FUNC',
                            valuePath: 'lookupCode',
                            labelPath: 'meaning',
                            placeholder: 'Выберите опцию',
                            useFirstOptionAfterEntriesChanged: true,
                            noUseFirstOptionWhenValueIsSet: true,
                            isClearable: true,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
                <Form.Item
                    rules={[{required: true}]}
                    style={{width: 100}}
                    label="Скрыто"
                    name={[field.name, 'hiddenFlag']}
                    initialValue={false}
                >
                    <CustomSelect
                        entries={[
                            {label: 'Да', value: true},
                            {label: 'Нет', value: false},
                        ]}
                        settings={{
                            placeholder: 'Выберите опцию',
                            isClearable: true,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
                <Form.Item
                    rules={[{required: true}]}
                    style={{width: 160}}
                    label="Расположение"
                    name={[field.name, 'subtotalLocation']}
                >
                    <CustomSelect
                        settings={{
                            url: 'lookupValue/TABLE_REPORT_SUBTOTAL_ALIGN',
                            valuePath: 'lookupCode',
                            labelPath: 'meaning',
                            placeholder: 'Выберите опцию',
                            useFirstOptionAfterEntriesChanged: true,
                            noUseFirstOptionWhenValueIsSet: true,
                            isClearable: true,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
                {templateCode && (
                    <>
                        <Form.Item
                            rules={[{required: true}]}
                            style={{width: 226}}
                            label="Размещение значения"
                            name={[field.name, 'align']}
                            initialValue="LEFT_MIDDLE"
                        >
                            <CustomSelect
                                settings={{
                                    url: 'lookupValue/TABLE_REPORT_CELL_ALIGN',
                                    valuePath: 'lookupCode',
                                    labelPath: 'meaning',
                                    placeholder: 'Выберите опцию',
                                    useFirstOptionAfterEntriesChanged: true,
                                    noUseFirstOptionWhenValueIsSet: true,
                                    isClearable: true,
                                }}
                                fixedDropdown={false}
                            />
                        </Form.Item>
                        <Form.Item
                            rules={[{required: true}]}
                            label="Стиль"
                            style={{width: 246}}
                            name={[field.name, 'style']}
                            initialValue="FONT_NORMAL"
                        >
                            <CustomSelect
                                settings={{
                                    url: 'lookupValue/TABLE_REPORT_CELL_STYLE',
                                    valuePath: 'lookupCode',
                                    labelPath: 'meaning',
                                    placeholder: 'Выберите стиль оформления',
                                    isClearable: true,
                                }}
                                fixedDropdown={false}
                            />
                        </Form.Item>
                    </>
                )}
                <Form.Item
                    rules={[{required: true}]}
                    style={{width: 180}}
                    label="Вид сортировки"
                    name={[field.name, 'sortingDirection']}
                    initialValue="ASC"
                >
                    <CustomSelect
                        settings={{
                            url: 'lookupValue/TABLE_REPORT_SORT_DIRECTION',
                            valuePath: 'lookupCode',
                            labelPath: 'meaning',
                            placeholder: 'Выберите опцию',
                            useFirstOptionAfterEntriesChanged: true,
                            noUseFirstOptionWhenValueIsSet: true,
                            isClearable: true,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
                <Form.Item
                    rules={[{required: true}]}
                    style={{width: 280}}
                    label="Расположение вывода итога/подытога"
                    name={[field.name, 'placeholderColumnName']}
                >
                    <CustomSelect
                        entries={attributesEntries}
                        settings={{
                            formInstance: form,
                            formFieldKey: 'placeholderColumnName',
                            placeholder: 'Выберите атрибут',
                            isClearable: true,
                        }}
                        fixedDropdown={false}
                    />
                </Form.Item>
            </div>
        ))}
    </div>
);
